/**
 * Utility functions for AddChartModal form
 * Reuses ColumnMetadata infrastructure from add-stat-card-modal
 */

import type { ChartType } from "@hooks/use-reports/types";
import type { ColumnMetadata, ValidationResult } from "../add-stat-card-modal/utils";

export type { ColumnMetadata, ValidationResult } from "../add-stat-card-modal/utils";
export { getColumnMetadata, filterColumnsForDimension } from "../add-stat-card-modal/utils";

/**
 * Dimension requirements for a given chart type
 */
export interface ChartDimensionRequirement {
  count: number;
  labels: string[];
  description: string;
}

/**
 * Get the dimension count and labels for each chart type
 */
export function getDimensionRequirementsForChart(chartType: ChartType): ChartDimensionRequirement {
  switch (chartType) {
    case "bar":
      return {
        count: 1,
        labels: ["Group by field"],
        description: "Select 1 field to group bars by",
      };
    case "pie":
      return {
        count: 1,
        labels: ["Group by field"],
        description: "Select 1 field to group slices by",
      };
    case "line":
      return {
        count: 1,
        labels: ["X-axis field"],
        description: "Select 1 field for the x-axis",
      };
    case "stacked-bar":
      return {
        count: 2,
        labels: ["Primary field (x-axis)", "Secondary field (stack)"],
        description: "Select 2 fields — primary groups x-axis bars, secondary groups stacked segments",
      };
    case "heatmap":
      return {
        count: 2,
        labels: ["Row field", "Column field"],
        description: "Select 2 fields — rows and columns of the heatmap",
      };
    case "multi-line":
      return {
        count: 2,
        labels: ["X-axis field", "Series field"],
        description: "Select 2 fields — x-axis values and the series to split lines by",
      };
    default:
      return {
        count: 1,
        labels: ["Field"],
        description: "Select a field",
      };
  }
}

/**
 * Filter columns available for a chart dimension slot.
 * All groupable columns are valid for chart dimensions.
 */
export function filterColumnsForChartDimension(
  allColumns: ColumnMetadata[],
  _chartType: ChartType,
  _dimensionIndex: number
): ColumnMetadata[] {
  // For charts, all groupable columns are valid in any slot
  return allColumns.filter((col) => col.isGroupable);
}

/**
 * Validate chart dimensions based on the chart type requirements
 */
export function validateChartDimensions(
  selectedDimensionIds: (string | null | undefined)[],
  chartType: ChartType
): ValidationResult {
  const requirements = getDimensionRequirementsForChart(chartType);
  const selectedCount = selectedDimensionIds.filter((id) => id !== null && id !== undefined && id !== "").length;

  if (selectedCount < requirements.count) {
    return {
      isValid: false,
      errorMessage: `${chartType} requires ${requirements.count} field(s); you have selected ${selectedCount}`,
    };
  }

  if (selectedCount > requirements.count) {
    return {
      isValid: false,
      errorMessage: `${chartType} requires ${requirements.count} field(s); you have selected ${selectedCount}`,
    };
  }

  return { isValid: true };
}

